import React from 'react';

import { InfoMessage, WarningMessage } from '@eika/attention-message';
import { LinkButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { IconTypes } from '@eika/icon';
import { AttentionLabel } from '@eika/label';

import { useConfig } from '../../defaultConfig';
import useTranslation from '../../hooks/useTranslation';
import { paymentTrackIDs } from '../../utils/gtm/trackIDs/payment';

import './AvtaleGiroDetailsFooter.scss';

interface Props {
    paymentOperationPending: boolean;
    isBlocked: boolean;
    onDelete: () => void;
    hasStateChange: boolean;
    isProposal?: boolean;
}

const AvtalegiroDetailsFooter: React.FC<Props> = ({
    isProposal = false,
    hasStateChange,
    isBlocked,
    onDelete,
    paymentOperationPending,
}) => {
    const { featureToggles } = useConfig();
    const translated = useTranslation();
    if (featureToggles.disableAvtaleGiroChanges) {
        return (
            <InfoMessage
                className="avtalegiro-details__info-message"
                text={translated.disableAvtaleGiroChanges.changeAndDeleteUnavailable}
                icon={IconTypes.INFO_24}
            />
        );
    }
    if (isBlocked) {
        return (
            <WarningMessage
                className="avtalegiro-details__info-message"
                text={translated.avtaleGiroAgreement.blocked}
                icon={IconTypes.STOPP_24}
            />
        );
    }

    const trackID = isProposal ? paymentTrackIDs.avtalegiro.update : paymentTrackIDs.avtalegiro.proposalUpdate;

    return (
        <>
            {featureToggles.showAvtalegiroDelay && (
                <AttentionLabel
                    className="avtalegiro-details__delay-message"
                    icon={IconTypes.TIMEGLASS_24}
                    text={translated.avtaleGiroAgreement.delayMessage}
                />
            )}
            {hasStateChange ? (
                <div className="update-avtalegiro__update-container">
                    <ButtonWithSpinner
                        buttonType="positive"
                        className="update-avtalegiro__update__submit"
                        type="submit"
                        showSpinner={paymentOperationPending}
                        data-testid="avtalegiro-update-button"
                        data-track-id={trackID}
                        data-track-linkname="submit"
                    >
                        {!isProposal ? translated.avtaleGiroAgreement.update : translated.avtaleGiroProposals.update}
                    </ButtonWithSpinner>
                </div>
            ) : (
                <div className="update-avtalegiro__delete-button-container">
                    <LinkButton
                        icon={IconTypes.SLETT_24}
                        onClick={onDelete}
                        data-track-id={trackID}
                        data-track-linkname={translated.delete}
                    >
                        {translated.delete}
                    </LinkButton>
                </div>
            )}
        </>
    );
};

export default AvtalegiroDetailsFooter;
